import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom'

import { FormHandles } from '@unform/core'
import { t } from 'i18next'
import debounce from 'lodash/debounce'

import { FeedbackContext } from '~/components/feedback'
import { formatMoneyV2, handleValidationsErrors, history } from '~/helpers'
import { useAppInfo } from '~/hooks/useAppInfo'
import useQueryParams from '~/hooks/useQuery'
import { useMarketplaceBalance } from '~/modules/retailMedia/hooks/useMarketplaceBalance'
import { requiredBillingAddressTypes } from '~/modules/retailMedia/pages/walltets/recharge/dtos/constants'
import { rechargeIndustryWallet } from '~/modules/retailMedia/store/industry/wallet/actions'

import MercadoPagoForm from '../components/MercadoPagoForm'
import { rechargeDTO } from '../dtos/recharge'
import {
  FormAddress,
  FormPerson,
  FormIndustryRechargeSchema
} from '../validation'
import { debounceDelay, successStatuses } from './constants'
import { initialDataDebug } from './mock'

export interface IRechargeFormContextData extends RechargeFormContextData<FormHandles> {}

const RechargeFormContext = createContext<IRechargeFormContextData>(
  {} as IRechargeFormContextData
)

const RechargeFormProvider = ({ children }: RechargeFormProviderProps) => {
  const dispatch = useDispatch()
  const { toast } = useContext(FeedbackContext)

  const { paymentProvider, rechargeMinValue, currencyUseCents } = useAppInfo()

  const [loadingSubmit, setLoadingSubmit] = useState(false)
  const [paymentRefused, setPaymentRefused] = useState(false)

  const [addressStepIsValid, setAddressStepIsValid] = useState(false)
  const [personStepIsValid, setPersonStepIsValid] = useState(false)

  const [cardToken, setCardToken] = useState<string | null>(null)

  const [formIsReadyToSubmit, setFormIsReadyToSubmit] = useState(false)

  const { id } = useParams()
  const publisherId = useQueryParams().get('rmid') as PublisherId
  const debugMode = useQueryParams().get('debug') === '1'

  /**
   * Handle amount
   */
  const [amount, setAmount] = useState(0)
  const [amountStepIsValid, setAmountStepIsValid] = useState(false)

  const amountFormatted = useMemo(
    () => formatMoneyV2({ value: amount }),
    [amount]
  )

  const handleChangeAmount = useCallback(
    ({ amount, isValid }: HandleChangeAmountProps) => {
      setAmountStepIsValid(isValid)
      setAmount(amount)
    },
    []
  )

  // If amount on serach params, call handleChangeAmount
  const amountParam = useQueryParams().get('amount') as string
  useEffect(() => {
    if (amountParam) {
      const amount = Number(amountParam)
      const isValid = amount >= rechargeMinValue
      handleChangeAmount({ amount, isValid })
    }
  }, [amountParam, handleChangeAmount, rechargeMinValue])

  /**
   * Handle credit card method
   */
  const [cardStepIsValid, setCardStepIsValid] = useState(false)
  const [cardBrand, setCardBrand] = useState<string>()

  const handleChangeCardForm = ({
    isValid,
    brand
  }: HandleChangeCardFormProps) => {
    setCardStepIsValid(isValid)
    setCardBrand(brand)
  }

  /**
   * Handle payment method
   */
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null)

  const handleChangePaymentMethod = (method: PaymentMethod) =>
    setPaymentMethod(method)

  const paymentSectionIsValid = useMemo(() => {
    if (paymentMethod === 'credit_card' && !cardStepIsValid) return false
    if (paymentMethod && cardStepIsValid) return true
    if (!paymentMethod) return false
    return true
  }, [paymentMethod, cardStepIsValid])

  /**
   * Handle card tokenization (for MercadoPago)
   */
  type CreateTokenFn = () => Promise<string>
  const [createTokenFn, setCreateTokenFn] = useState<CreateTokenFn | null>(null)

  /**
   * Handle marketplace balance
   */

  const {
    data: balanceMarketplaceData,
    isPending: isPendingMarketplaceBalance,
    error: errorMarketplaceBalance
  } = useMarketplaceBalance()

  const balanceMarketplaceIsInsufficient = useMemo(
    () =>
      !balanceMarketplaceData?.total ||
      amount > Number(balanceMarketplaceData?.total),
    [amount, balanceMarketplaceData]
  )

  /**
   * Handle address step
   */

  const addressStepIsRequired = useMemo(
    () => requiredBillingAddressTypes.includes(paymentMethod),
    [paymentMethod]
  )

  /**
   * Handle Mercado Pago strategy
   */

  const isMercadoPagoActive = useMemo(
    () => paymentProvider === 'mercado_pago' && paymentMethod === 'credit_card',
    [paymentProvider, paymentMethod]
  )

  /**
   * Handle changes on form
   */
  const handleChangeForm = debounce(e => {
    const shouldValidateCard = paymentMethod === 'credit_card'

    const validators = [
      {
        schema: FormAddress,
        setValid: setAddressStepIsValid,
        shouldValidate: ['credit_card', 'marketplace'].includes(paymentMethod)
      },
      {
        schema: FormPerson,
        setValid: setPersonStepIsValid,
        shouldValidate: ['credit_card', 'pix', 'marketplace'].includes(
          paymentMethod
        )
      }
    ]

    const { value, name } = e.target || e

    const formData = formRef?.current?.getData() || {}

    const data = { ...formData, [name]: value }

    const valid = validators
      .map(({ schema, setValid, shouldValidate }) => {
        setValid(false)
        try {
          if (!shouldValidate) return true

          schema.validateSync(data, { abortEarly: false })
          setValid(true)
          return true
        } catch (error) {
          console.debug('🚀 ~ handleValidation ~ error:', error)
          return false
        }
      })
      .every(Boolean)

    if (shouldValidateCard) setFormIsReadyToSubmit(valid && cardStepIsValid)
    else setFormIsReadyToSubmit(valid)
  }, debounceDelay)

  /**
   * Handle submit
   * Esse submit tem 2 comportamentos.
   * 1. Clássico onde pega os dados do formulário e repassa para API
   * 2. MercadoPago onde pega os dados do formulário e repassa para API após o tokenização
   * 2.1. Nesse caso, temos um passo intermediário onde aguardamos o tokenização do cartão
   */
  const formRef = useRef<FormHandles>(null)

  /**
   * Pré-submit - Caso MercadoPago
   */

  const pendingSubmitDataRef = useRef<RechargeFormData | null>(null)
  const [waitingForToken, setWaitingForToken] = useState(false)

  /**
   * Submit handle extras
   */

  const handleSubmitRefused = useCallback(() => {
    toast.critical(t('rm:recharge.transaction.instructions.paymentError'))

    setLoadingSubmit(false)
    setPaymentRefused(true)
  }, [toast])

  const handleSuccess = useCallback(
    ({ data: response }: { data: ResponseRecharge; status: number }) => {
      const isSuccess = successStatuses.includes(response.status)

      if (isSuccess) {
        history.push(
          `/retail-media/wallet/${id}/transaction/${response.id}?rmid=${publisherId}`
        )
        return
      }

      const isRefused = response.status === 'payment_refused'

      if (isRefused) {
        handleSubmitRefused()
      }

      history.push(
        `/retail-media/wallet/${id}/transactions?rmid=${publisherId}`
      )
    },
    [id, publisherId, handleSubmitRefused]
  )

  /**
   * Final submit
   */

  const onSubmitFinal = useCallback(
    async (data: RechargeFormData) => {
      /**
       * Handle amount
       */
      if (!amountStepIsValid) {
        toast.critical(
          t('rm:wallet.recharge.error.minimumValue.message', {
            value: formatMoneyV2({ value: rechargeMinValue })
          })
        )

        setLoadingSubmit(false)
        return
      }

      /**
       * Reset states
       */
      setLoadingSubmit(true)
      setPaymentRefused(false)

      /**
       * Handle data
       */
      const amountRaw = data.amount?.numAsString
      const amount = currencyUseCents
        ? Number(amountRaw) / 100
        : Number(amountRaw)

      const payload: RechargeDTOData = {
        ...data,
        payment_method: data.payment_method || paymentMethod,
        amount,
        publisher_id: publisherId,
        card_brand: cardBrand,
        card_token: cardToken || undefined
      }

      /**
       * Handle validation
       */
      try {
        FormIndustryRechargeSchema[paymentMethod].validateSync(payload, {
          abortEarly: false
        })

        if (!cardStepIsValid && paymentMethod === 'credit_card') {
          throw new Error(t('rm:invalidCard'))
        }
      } catch (error) {
        handleValidationsErrors(error, formRef)

        setLoadingSubmit(false)

        return
      }

      /**
       * Handle DTO
       */

      const body = rechargeDTO(payload)

      if (debugMode) {
        console.debug('🚀 ~ FormRecharge ~ body:', body)
        setLoadingSubmit(false)
        return
      }

      /**
       * Handle dispatch
       */
      const deviceId = window?.MP_DEVICE_SESSION_ID || undefined

      dispatch(
        rechargeIndustryWallet({
          body,
          paymentProvider,
          deviceId,
          showSuccessCallback: ({ data: response }) =>
            successStatuses.includes(response.status),
          onError: handleSubmitRefused,
          onSuccess: handleSuccess
        })
      )
    },
    [
      amountStepIsValid,
      cardBrand,
      cardStepIsValid,
      cardToken,
      debugMode,
      dispatch,
      handleSubmitRefused,
      handleSuccess,
      currencyUseCents,
      paymentMethod,
      paymentProvider,
      publisherId,
      rechargeMinValue,
      toast
    ]
  )

  /**
   * Middleware for MercadoPago
   */
  const [initialDataMP, setInitialDataMP] = useState<RechargeFormData | null>(
    null
  )
  const handleMercadoPagoSubmit = useCallback(
    (data: RechargeFormData) => {
      if (cardToken) {
        onSubmitFinal(data)
        return
      }

      // Persit data to wait for token
      // useEffect will continue when cardToken changes
      pendingSubmitDataRef.current = data
      setInitialDataMP(data)
      setWaitingForToken(true)
      setLoadingSubmit(true)
    },
    [cardToken, onSubmitFinal, setWaitingForToken, setLoadingSubmit]
  )

  /**
   * On submit - Flow triage
   */
  const handleSubmit = useCallback(
    (data: RechargeFormData) => {
      if (debugMode) console.debug('🚀 ~ FormRecharge ~ FormData:', data)

      // If MercadoPago is active, wait for the card token
      if (isMercadoPagoActive) {
        handleMercadoPagoSubmit(data)
        return
      }

      onSubmitFinal(data)
    },
    [isMercadoPagoActive, debugMode, onSubmitFinal, handleMercadoPagoSubmit]
  )

  /**
   * Submit listener
   */
  // Quando o cardToken ficar disponível e estivermos aguardando, prosseguir com o submit
  useEffect(() => {
    if (cardToken && waitingForToken && pendingSubmitDataRef.current) {
      setWaitingForToken(false)
      onSubmitFinal(pendingSubmitDataRef.current)
      pendingSubmitDataRef.current = null
    }
  }, [cardToken, waitingForToken, onSubmitFinal])

  const initialData: RechargeFormInitialData = useMemo(
    () => ({
      amount,
      ...(debugMode ? initialDataDebug : {})
    }),
    [amount, debugMode]
  )

  return (
    <RechargeFormContext.Provider
      value={{
        debugMode,

        formRef,

        amount,
        amountFormatted,
        amountStepIsValid,
        handleChangeAmount,

        paymentMethod,
        handleChangePaymentMethod,
        paymentSectionIsValid,
        cardStepIsValid,
        cardBrand,
        handleChangeCardForm,

        createTokenFn,
        setCreateTokenFn,
        setCardToken,

        balanceMarketplaceData,
        isPendingMarketplaceBalance,
        errorMarketplaceBalance,
        balanceMarketplaceIsInsufficient,

        addressStepIsRequired,
        addressStepIsValid,
        personStepIsValid,
        formIsReadyToSubmit,

        isMercadoPagoActive,
        cardToken,

        handleChangeForm,

        handleSubmit,
        loadingSubmit,
        paymentRefused,

        initialData,
        initialDataMP
      }}
    >
      {children}

      {isMercadoPagoActive && <MercadoPagoForm />}
    </RechargeFormContext.Provider>
  )
}

function useRechargeForm(): IRechargeFormContextData {
  const context = useContext(RechargeFormContext)

  if (!context) {
    throw new Error(
      'useRechargeForm must be used within an RechargeFormProvider'
    )
  }

  return context
}

export { RechargeFormContext, RechargeFormProvider, useRechargeForm }
